/**
 * Excel export of the Profit & Loss account cards report.
 *
 * Styling and assembly follow the trial balance export
 * (Components/LedgerReport/TrialBalanceReportHtml): an array-of-arrays is built while a
 * parallel `rowMeta` records the kind of each row, then styles, merges, row heights and
 * the RTL sheet view are applied per kind. The shared `applyWorkbookRtl` helper is reused
 * so the file opens right-to-left.
 *
 * The workbook has a single sheet laid out like the account card (כרטסת) Excel export:
 * the same journal columns (details, command, references, dates, counter account, then
 * debit / credit / balance), with the report as displayed — sections, palcodes and every
 * subtotal — and each account card row followed by its detailed transactions and their total.
 */

import * as XLSX from 'xlsx-js-style';
import { applyWorkbookRtl, AMOUNT_NUMBER_FORMAT } from '../../../Utils/ExcelReport';
import { getCaseNumberLabel } from '../../../Utils/ReportUtils';

const PALETTE = {
    primary: 'FF2F4BFF',
    primaryDark: 'FF1E308F',
    headerFont: 'FFFFFFFF',
    opening: 'FFEFF2FF',
    subFill: 'FFE3E9FF',
    totalFill: 'FFD7DFFF',
    groupFill: 'FFBECBFF',
    grandFill: 'FFA9BBFF',
    stripe: 'FFF6F8FF',
    detail: 'FFF0F1F6',
    text: 'FF1F2937',
    muted: 'FF55596B',
    border: 'FFC3CBDB',
};

const thinBorder = { style: 'thin', color: { rgb: PALETTE.border } };
const allThin = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

const STYLES = {
    clientLine: { font: { bold: true, sz: 14, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right' } },
    reportTitle: { font: { bold: true, sz: 20, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center' } },
    dateLine: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
    metaLine: { font: { italic: true, sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
    sectionHeader: { font: { bold: true, sz: 15, color: { rgb: PALETTE.primaryDark } }, alignment: { horizontal: 'right', vertical: 'center' } },
    sectionTitle: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primary }, underline: true }, alignment: { horizontal: 'right', vertical: 'center' } },
    palCodeLabel: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.opening } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    header: { font: { bold: true, sz: 12, color: { rgb: PALETTE.headerFont } }, fill: { fgColor: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: allThin },
    dataText: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    dataNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    stripeText: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    stripeNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    detailText: { font: { sz: 10, color: { rgb: PALETTE.muted } }, fill: { fgColor: { rgb: PALETTE.detail } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    detailNum: { font: { sz: 10, color: { rgb: PALETTE.muted } }, fill: { fgColor: { rgb: PALETTE.detail } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    cardText: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    cardNum: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    cardTotalText: { font: { bold: true, sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.detail } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    cardTotalNum: { font: { bold: true, sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.detail } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    subtotalText: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    subtotalNum: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    categoryText: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.subFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    categoryNum: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.subFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    groupText: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    groupNum: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    grandText: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.grandFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    grandNum: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.grandFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
};

const ROW_HEIGHT = {
    clientLine: 24, reportTitle: 32, dateLine: 20, metaLine: 18,
    sectionHeader: 24, sectionTitle: 22, palCodeLabel: 20, header: 26,
    data: 18, stripe: 18, detail: 16, card: 22, cardTotal: 20, subtotal: 22, category: 22,
    group: 24, grand: 26, blank: 8,
};

const MERGED_KINDS = ['clientLine', 'reportTitle', 'dateLine', 'metaLine', 'sectionHeader', 'sectionTitle', 'palCodeLabel'];

const KIND_STYLES = {
    data: ['dataText', 'dataNum'],
    stripe: ['stripeText', 'stripeNum'],
    detail: ['detailText', 'detailNum'],
    card: ['cardText', 'cardNum'],
    cardTotal: ['cardTotalText', 'cardTotalNum'],
    subtotal: ['subtotalText', 'subtotalNum'],
    category: ['categoryText', 'categoryNum'],
    group: ['groupText', 'groupNum'],
    grand: ['grandText', 'grandNum'],
};

/** Shared sheet writer: applies merges, widths, heights, RTL and per-kind cell styles. */
const createStyledSheet = ({ aoa, rowMeta, merges, colCount, colWidths, numericColIndexes }) => {
    const numericSet = new Set(numericColIndexes);
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!merges'] = merges.filter(Boolean);
    ws['!cols'] = Array.from({ length: colCount }, (_, c) => ({ wch: colWidths[c] ?? 18 }));
    ws['!rows'] = rowMeta.map((meta) => ({ hpt: ROW_HEIGHT[meta.kind] || 18 }));
    ws['!views'] = [{ RTL: true }];

    aoa.forEach((row, r) => {
        const meta = rowMeta[r];
        if (!meta || meta.kind === 'blank') return;

        if (MERGED_KINDS.includes(meta.kind)) {
            const addr = XLSX.utils.encode_cell({ r, c: 0 });
            if (ws[addr]) ws[addr].s = STYLES[meta.kind];
            return;
        }

        for (let c = 0; c < colCount; c += 1) {
            const addr = XLSX.utils.encode_cell({ r, c });
            let cell = ws[addr];
            if (!cell) { cell = { t: 's', v: '' }; ws[addr] = cell; }

            const isNum = numericSet.has(c);
            if (meta.kind === 'header') {
                cell.s = STYLES.header;
            } else {
                const [textStyle, numStyle] = KIND_STYLES[meta.kind] || KIND_STYLES.data;
                cell.s = isNum ? STYLES[numStyle] : STYLES[textStyle];
            }
            if (isNum && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
        }
    });

    return ws;
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
const sumField = (list, key) => list.reduce((total, item) => total + (Number(item[key]) || 0), 0);

/**
 * The report in Profit & Loss order, using the account card export's journal columns.
 * The first column holds the account card, repeated on each of its transaction rows so a
 * row keeps its card when the sheet is filtered or sorted. Account card, palcode and section
 * totals put their balance under the balance column (and the % of sales in its own last
 * column), so every amount of a kind lines up vertically.
 */
const buildReportSheet = ({ model, titleLines, showPercentage, cardLabel, formatDate, t }) => {
    const columns = [
        { label: t('AccountCard'), width: 32 },
        { label: t('פרטים'), width: 36 },
        { label: t('פקודה חודש/שנה'), width: 16 },
        { label: t('אסמכתא 1'), width: 14 },
        { label: t('אסמכתא 2'), width: 14 },
        { label: t('תאריך מסמך'), width: 14 },
        { label: t('תאריך רישום'), width: 14 },
        { label: t('חשבון נגדי'), width: 24 },
        { label: 'חובה', width: 18 },
        { label: 'זכות', width: 18 },
        { label: 'יתרה', width: 18 },
        ...(showPercentage ? [{ label: t('PercentOfSales'), width: 16 }] : []),
    ];
    const colCount = columns.length;
    const DEBIT_COL = 8;
    const CREDIT_COL = 9;
    const BALANCE_COL = 10;
    const PERCENT_COL = 11;

    const aoa = [];
    const rowMeta = [];
    const merges = [];
    const blankRow = () => new Array(colCount).fill('');

    const pushMerged = (text, kind) => {
        const r = aoa.length;
        const row = blankRow();
        row[0] = text;
        aoa.push(row);
        rowMeta.push({ kind });
        merges.push({ s: { r, c: 0 }, e: { r, c: Math.max(colCount - 1, 0) } });
    };
    const pushAmountsRow = (label, { debit = '', credit = '', balance = '', percentage = '' }, kind) => {
        const row = blankRow();
        row[0] = label;
        row[DEBIT_COL] = debit;
        row[CREDIT_COL] = credit;
        row[BALANCE_COL] = balance;
        if (showPercentage) row[PERCENT_COL] = percentage;
        aoa.push(row);
        rowMeta.push({ kind });
    };
    const pushHeader = () => {
        aoa.push(columns.map((column) => column.label));
        rowMeta.push({ kind: 'header' });
    };
    const pushBlank = () => { aoa.push(blankRow()); rowMeta.push({ kind: 'blank' }); };

    const pushTransactions = (card) => {
        card.transactions.forEach((transaction, index) => {
            const row = [
                cardLabel(card),
                transaction.description ? t(transaction.description, { defaultValue: transaction.description }) : '',
                transaction.command ?? '',
                transaction.reference1 ?? '',
                transaction.reference2 ?? '',
                transaction.documentDate ? formatDate(transaction.documentDate) : '',
                transaction.uploadDate ? formatDate(transaction.uploadDate) : '',
                transaction.countraAccount || '-',
                round2(transaction.debitAccount),
                round2(transaction.creditAccount),
                round2(transaction.balance),
            ];
            if (showPercentage) row.push('');
            aoa.push(row);
            rowMeta.push({ kind: index % 2 === 1 ? 'stripe' : 'data' });
        });

        pushAmountsRow(
            `${t('Total')}: ${cardLabel(card)}`,
            {
                debit: round2(sumField(card.transactions, 'debitAccount')),
                credit: round2(sumField(card.transactions, 'creditAccount')),
                balance: round2(card.transactionsTotal),
            },
            'cardTotal'
        );
    };

    titleLines.forEach((line) => pushMerged(line.text, line.kind));
    pushBlank();

    model.sections.forEach((section) => {
        if (section.showAgLabel) pushMerged(section.agLabel, 'sectionHeader');
        pushMerged(section.detailLabel, 'sectionTitle');
        pushHeader();

        section.palCodes.forEach((palCode) => {
            pushMerged(palCode.palCodeLabel, 'palCodeLabel');
            palCode.cards.forEach((card) => {
                pushAmountsRow(cardLabel(card), { balance: round2(card.balance), percentage: card.percentage }, 'card');
                if (card.transactions.length) pushTransactions(card);
            });
            pushAmountsRow(
                `${t('Total')}: ${palCode.palCodeLabel}`,
                { balance: round2(palCode.total), percentage: palCode.percentage },
                'subtotal'
            );
        });

        pushAmountsRow(
            `${t('Total')}: ${section.categoryLabel}`,
            { balance: round2(section.categoryTotal), percentage: section.categoryPercentage },
            'category'
        );

        if (section.isLastCategoryInGroup) {
            pushAmountsRow(
                `${t('Total')} ${section.accountingGroupName}:`,
                { balance: round2(section.groupTotal), percentage: section.groupPercentage },
                'group'
            );
        }
        if (section.showRunningTotal) {
            pushAmountsRow(
                t('ProfitAndLossAfterGroup', { group: section.accountingGroupName }),
                { balance: round2(section.runningTotal), percentage: section.runningPercentage },
                'category'
            );
        }
        pushBlank();
    });

    pushAmountsRow(
        t('TotalProfitAndLossInReport'),
        { balance: round2(model.grandTotal), percentage: model.grandTotalPercentage },
        'grand'
    );

    return createStyledSheet({
        aoa,
        rowMeta,
        merges,
        colCount,
        colWidths: columns.map((column) => column.width),
        numericColIndexes: showPercentage
            ? [DEBIT_COL, CREDIT_COL, BALANCE_COL, PERCENT_COL]
            : [DEBIT_COL, CREDIT_COL, BALANCE_COL],
    });
};

export const downloadProfitAndLossCardsReportExcel = ({
    model,
    client,
    trialBalanceData,
    title,
    period,
    showPercentage,
    showAccountCodeId,
    formatDate,
    t,
}) => {
    if (!model?.sections?.length) return false;

    const cardLabel = (card) => {
        const name = card.accountCardName || '';
        return showAccountCodeId && card.accountCardId ? `${card.accountCardId} ${name}`.trim() : name;
    };

    const clientName = trialBalanceData?.clientName || client?.name || '';
    const clientCase = trialBalanceData?.clientCase || client?.caseId || '';
    const caseLabel = getCaseNumberLabel(client?.caseType);

    const titleLines = [
        { text: `${caseLabel}: ${clientCase}    ${clientName}`, kind: 'clientLine' },
        { text: title, kind: 'reportTitle' },
        { text: period, kind: 'dateLine' },
        { text: `${t('TotalIncome')}: ${model.totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, kind: 'metaLine' },
    ].filter((line) => line.text);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
        wb,
        buildReportSheet({ model, titleLines, showPercentage, cardLabel, formatDate, t }),
        'ProfitAndLoss'
    );

    applyWorkbookRtl(wb);
    XLSX.writeFile(wb, `${clientName || 'Report'}_ProfitAndLossCards.xlsx`);
    return true;
};

export default downloadProfitAndLossCardsReportExcel;
